/**
 * Jira Team 选项（appSettings key: jiraTeamOptions）的解析与校验。
 *
 * 配置形态：项目级 CSV 文本，每行一条「团队名,Team UUID」。
 * 之所以用文本而不是结构化存储：客户希望这份清单能在 Jira / Excel / 文档里自行保管，
 * 在我们平台直接复制粘贴，也随时能复制走。
 *
 * --story=1020421727138529159 【CS】【AI】JIRA 集成自定义字段适配（Wonderland 必填字段 Team 取不到导致无法提单）
 */

const UUID_REG = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * 团队页 URL 里的 UUID 必须优先按路径段取：
 * home.atlassian.com/o/<orgId>/people/team/<teamUuid>?cloudId=<cloudId>
 * 整串里有三个 UUID，取"第一个"会拿到 orgId（实测踩过）。
 */
const TEAM_URL_REG = /\/people\/team\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

const HEADER_NAME_REG = /^(team\s*name|team|团队名(称)?|name)$/i;

/** 表头第二列的样子（只在首行判定，避免把一条正常的非法数据行当成表头静默吞掉） */
const UUID_HEADER_REG = /^(team\s*)?(uuid|id|team\s*id|标识)$/i;

/** 每行最多解析出的列数（多余列忽略） */
const MAX_COLUMNS = 2;

/**
 * 从一段文本里抽出 Team UUID。
 * 支持：裸 UUID、ari:cloud:identity::team/<uuid>、团队页 URL（/people/team/<uuid>）
 * @param {string} raw
 * @returns {string|null}
 */
export function extractTeamUuid(raw) {
  if (typeof raw !== 'string') { return null; }
  const fromUrl = raw.match(TEAM_URL_REG);
  if (fromUrl) { return fromUrl[1]; }
  const match = raw.match(UUID_REG);
  return match ? match[0] : null;
}

function splitColumns(line) {
  // Tab 优先（从 Excel 直接粘贴）、竖线次之：这两种不会有"团队名里带分隔符"的歧义
  if (line.indexOf('\t') >= 0) {
    return line.split('\t').slice(0, MAX_COLUMNS).map(x => x.trim());
  }
  if (line.indexOf('|') >= 0) {
    return line.split('|').slice(0, MAX_COLUMNS).map(x => x.trim());
  }
  if (line.indexOf(',') < 0) {
    return [line.trim()];
  }
  // 逗号分隔必须引号感知：团队名里可能有逗号（toTeamCsvText 会加引号包裹），
  // 直接 split(',') 会把「"Client, Team",uuid」切成三段，团队名被截断、UUID 也错位
  const cols = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === ',' && !inQuotes) {
      cols.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  cols.push(current.trim());
  return cols.slice(0, MAX_COLUMNS);
}

function stripQuotes(value) {
  const v = String(value == null ? '' : value).trim();
  if (v.length >= 2 && ((v[0] === '"' && v[v.length - 1] === '"') || (v[0] === "'" && v[v.length - 1] === "'"))) {
    return v.slice(1, -1).trim();
  }
  return v;
}

/**
 * 解析 Team 选项文本。
 * @param {string} text 原始 CSV 文本
 * @returns {{items: Array<{name: string, uuid: string}>, invalid: Array<{line: number, raw: string, reason: string}>, duplicated: Array<{line: number, name: string, uuid: string}>}}
 */
export function parseTeamOptions(text) {
  const items = [];
  const invalid = [];
  const duplicated = [];
  if (typeof text !== 'string' || !text.trim()) {
    return { items, invalid, duplicated };
  }

  const seenUuid = Object.create(null);
  const seenName = Object.create(null);
  text.split(/\r?\n/).forEach((rawLine, index) => {
    const lineNo = index + 1;
    const line = rawLine.trim();
    if (!line || line.startsWith('#')) { return; }

    const cols = splitColumns(line);
    const name = stripQuotes(cols[0]);
    const uuid = cols.length > 1 ? extractTeamUuid(stripQuotes(cols[1])) : null;

    // 表头只在首行判定：否则一条刚好叫 Team/Name 的非法数据行会被静默吞掉、用户看不到报错
    if (index === 0 && !uuid && (HEADER_NAME_REG.test(name) || UUID_HEADER_REG.test(cols[1] || ''))) { return; }

    if (!name) {
      invalid.push({ line: lineNo, raw: rawLine, reason: 'NAME_EMPTY' });
      return;
    }
    if (!uuid) {
      invalid.push({ line: lineNo, raw: rawLine, reason: 'UUID_INVALID' });
      return;
    }
    if (seenUuid[uuid] || seenName[name]) {
      duplicated.push({ line: lineNo, name, uuid });
      return;
    }
    seenUuid[uuid] = true;
    seenName[name] = true;
    items.push({ name, uuid });
  });

  return { items, invalid, duplicated };
}

/**
 * 是否 Jira 官方 Team 字段。
 * 后台会透出 schema.custom（com.atlassian.jira.plugin.system.customfieldtypes:atlassian-team
 * 或 com.atlassian.teams:rm-teams-custom-field-team）；type 在不同接口下可能是 team / any。
 * @param {{type?: string, custom?: string}} field
 * @returns {boolean}
 */
export function isJiraTeamField(field) {
  if (!field) { return false; }
  const custom = field.custom || '';
  if (custom.indexOf('atlassian-team') >= 0 || custom.indexOf('rm-teams-custom-field-team') >= 0) {
    return true;
  }
  return field.type === 'team';
}

/** 解析结果 → antd Select 的 options（label=团队名，value=UUID） */
export function toTeamSelectOptions(items) {
  return (items || []).map(x => ({ label: x.name, value: x.uuid }));
}

/** 序列化成 CSV 文本时的表头（中英文站点都能被 parseTeamOptions / parseTeamRows 识别为表头） */
export const TEAM_CSV_HEADER = 'Team Name,Team UUID';

/**
 * 把 CSV 文本解析成「行数组」（表格编辑模式的主数据）。
 * 与 parseTeamOptions 的区别：**非法行也会保留成一行**（带 error），
 * 这样从 CSV 模式切回表格时用户能看到并修改，不会被静默丢弃。
 *
 * @returns {Array<{key: string, name: string, uuid: string, raw: string, error: string|null}>}
 */
export function parseTeamRows(text) {
  const rows = [];
  if (typeof text !== 'string' || !text.trim()) {
    return rows;
  }
  text.split(/\r?\n/).forEach((rawLine, index) => {
    const line = rawLine.trim();
    if (!line || line.startsWith('#')) { return; }
    const cols = splitColumns(line);
    const name = stripQuotes(cols[0]);
    const uuidRaw = cols.length > 1 ? stripQuotes(cols[1]) : '';
    const uuid = extractTeamUuid(uuidRaw);
    // 表头只在首行判定
    if (index === 0 && !uuid && (HEADER_NAME_REG.test(name) || UUID_HEADER_REG.test(uuidRaw))) { return; }
    if (!name && !uuidRaw) { return; }

    let error = null;
    if (!name) {
      error = 'NAME_EMPTY';
    } else if (!uuid) {
      error = 'UUID_INVALID';
    }
    rows.push({
      key: `${index}-${uuid || uuidRaw || name}`,
      name,
      // 解析不出 UUID 时保留用户粘的原始值，方便他看到并改
      uuid: uuid || uuidRaw,
      raw: rawLine,
      error,
    });
  });
  return rows;
}

/** 行数组 → CSV 文本（存储契约不变：落库仍是 CSV 文本） */
export function toTeamCsvText(rows) {
  const lines = [];
  (rows || []).forEach((row) => {
    // 注释行原样保留，切模式来回切不至于把用户的备注弄丢
    if (((row && row.raw) || '').trim().startsWith('#')) {
      lines.push(row.raw);
      return;
    }
    const name = ((row && row.name) || '').trim();
    const uuid = ((row && row.uuid) || '').trim();
    if (!name && !uuid) { return; }
    // 团队名里可能有逗号/引号，不加引号会被下次解析截断
    const escapeCell = (v) => (v.indexOf(',') >= 0 || v.indexOf('"') >= 0 ? `"${v.replace(/"/g, '""')}"` : v);
    lines.push(`${escapeCell(name)},${escapeCell(uuid)}`);
  });
  // 一条都没有就存空串，避免库里只剩一行表头
  return lines.length === 0 ? '' : [TEAM_CSV_HEADER, ...lines].join('\n');
}

/** 找出重复行（同 UUID 或同名），返回 { rowKey: 'DUPLICATE_UUID' | 'DUPLICATE_NAME' } */
export function findDuplicateTeamRowKeys(rows) {
  const seenUuid = Object.create(null);
  const seenName = Object.create(null);
  const duplicates = Object.create(null);
  (rows || []).forEach((row) => {
    if (!row || row.error) { return; }
    if (row.uuid) {
      // 归一化后再判重：一行填 URL、另一行填裸 UUID，其实是同一个团队，必须判成重复
      const uuid = extractTeamUuid(row.uuid) || row.uuid.trim();
      if (seenUuid[uuid]) {
        duplicates[row.key] = 'DUPLICATE_UUID';
      } else {
        seenUuid[uuid] = true;
      }
    }
    const nameKey = (row.name || '').trim();
    if (nameKey) {
      if (seenName[nameKey]) {
        duplicates[row.key] = duplicates[row.key] || 'DUPLICATE_NAME';
      } else {
        seenName[nameKey] = true;
      }
    }
  });
  return duplicates;
}
